// src/App.tsx
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from "./components/ProtectedRoute";
import FamilyProtectedRoute from "./components/family/FamilyProtectedRoute";
import CreatorProtectedRoute from "./components/CreatorProtectedRoute";
import ConsentBanner from './components/ConsentBanner';
import { trackAnalyticsPageView } from './utils/analytics';

import LoginPage from './pages/LoginPage';
//import HomePage from "./HomePage";
import Impressum from './pages/Impressum';
import AdminDashboard from "./pages/AdminDashboard";
import AGB from './pages/AGB';
import HomePage from "./HomePage";
import Datenschutzerklaerung from './pages/Datenschutz';
import PatientProfilePage from './pages/PatientProfilePage'
import AudienceSelectionPage from "./pages/Website/pages/AudienceSelectionPage";
import FamilyLandingPlaceholder from "./pages/Website/pages/FamilyLandingPlaceholder";
import SteffiLanding2 from "./pages/Website/pages/Website";
import SteffiHome from "./pages/Steffi/SteffiHome";
import TalkingFaceLab from "./pages/Steffi/lab/TalkingFaceLab";
import CalendlyWidget from "./pages/Website/components/CalendlyWidget";
import CalendarPage from "./pages/CalendarPage";
import GeneralInfos from "./pages/Generalnfos";
import GeneralReminders from "./pages/GeneralReminders";
import UsermanagementPage from "./pages/UsermanagementPage";
import SettingsPage from "./pages/SettingsPage";
import CognitiveExercisesPage from "./pages/CognitiveExercisesPage";
import QuizzesPage from "./pages/QuizzesPage";
import QuizAnalysisPage from "./pages/QuizAnalysisPage";
import FamilyInviteAcceptPage from "./pages/family/FamilyInviteAcceptPage";
import FamilyLoginPage from "./pages/family/FamilyLoginPage";
import FamilyHomePage from "./pages/family/FamilyHomePage";
import FamilyPatientPage from "./pages/family/FamilyPatientPage";
import SteffiAvatarDemo from "./components/animation/Avatar";
import SteffiAvatarDemo2 from "./components/animation/SteffiAvatarDemo";
import PipecatVoiceBot from "./components/animation/Pipecat2";
import PipecatVoiceBotWithAvatar from "./components/animation/Pipecat3"; 

const PublicBlogIndex = lazy(() => import("./pages/blog/PublicBlogIndex"));
const PublicBlogPost = lazy(() => import("./pages/blog/PublicBlogPost"));
const CreatorShell = lazy(() => import("./pages/creator/CreatorShell"));
const CreatorDashboard = lazy(() => import("./pages/creator/CreatorDashboard"));
const BlogEditorPage = lazy(() => import("./pages/creator/BlogEditorPage"));
const BlogPreviewPage = lazy(() => import("./pages/creator/BlogPreviewPage"));
// Unlisted page, and the only one that pulls in the audio-recording code —
// lazy so it stays out of the main bundle for everybody else.
const Stimmspende = lazy(() => import("./pages/Stimmspende"));
const ReservationPage = lazy(() => import("./pages/Steffi/ReservationPage"));
const AboutPage = lazy(() => import("./pages/Steffi/AboutPage"));

function AnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    void trackAnalyticsPageView(location.pathname, location.search);
  }, [location.pathname, location.search]);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AnalyticsRouteTracker />
      <ConsentBanner />
      <Suspense fallback={<div className="p-6 text-sm text-gray-500">Seite wird geladen …</div>}>
        <Routes>
        <Route element={<ProtectedRoute />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/updates" element={<AdminDashboard />} />
          <Route path="/admin/monitoring" element={<AdminDashboard />} />
          <Route path="/admin/patients/:patientId" element={<AdminDashboard />} />
          <Route path="/admin/blog" element={<AdminDashboard />} />
          <Route path="/admin/blog/new" element={<AdminDashboard />} />
          <Route path="/admin/blog/:id/edit" element={<AdminDashboard />} />
          <Route path="/admin/blog/:id/preview" element={<BlogPreviewPage basePath="/admin" />} />
          <Route path="/admin/crm" element={<AdminDashboard />} />
          <Route path="/übersicht" element={<HomePage />} />
          <Route path="/patients/:id" element={<PatientProfilePage />} />
          <Route path="/calender" element={<CalendarPage />} />
          <Route path="/allgemeine-infos" element={<GeneralInfos />} />
          <Route path="/allgemeine-erinnerungen" element={<GeneralReminders />} />
          <Route path="/aktivierungen" element={<GeneralReminders activation />} />
          <Route path="/abfragen" element={<GeneralReminders query />} />
          <Route path="/nutzerverwaltung" element={<UsermanagementPage />} />
          <Route path="/einstellungen" element={<SettingsPage />} />
          <Route path="/cognitive-uebungen" element={<CognitiveExercisesPage />} />
          <Route path="/quizzes" element={<QuizzesPage />} />
          <Route path="/quizzes/analysis" element={<QuizAnalysisPage />} />

          {/* add more protected routes here */}
        </Route>

        <Route element={<FamilyProtectedRoute />}>
          <Route path="/family" element={<FamilyHomePage />} />
          <Route path="/family/patients/:id" element={<FamilyPatientPage />} />
          <Route path="/family/patients/:id/:tab" element={<FamilyPatientPage />} />
        </Route>

        <Route element={<CreatorProtectedRoute />}>
          <Route element={<CreatorShell />}>
            <Route path="/creator" element={<CreatorDashboard />} />
            <Route path="/creator/blog/new" element={<BlogEditorPage />} />
            <Route path="/creator/blog/:id/edit" element={<BlogEditorPage />} />
            <Route path="/creator/blog/:id/preview" element={<BlogPreviewPage />} />
          </Route>
        </Route>

        {/* Public routes */}
        <Route path="/talking-face" element={<TalkingFaceLab />} />
        <Route path="/SteffiAvatarDemo" element={<SteffiAvatarDemo/>}/>
        <Route path="/SteffiAvatarDemo2" element={<SteffiAvatarDemo2/>}/>
        <Route path="/PipecatVoiceBot" element={<PipecatVoiceBot webrtcUrl="http://localhost:7860/api/offer"/>}/>
        <Route path="/PipecatVoiceBotWithAvatar" element={<PipecatVoiceBotWithAvatar/>}/>

        <Route path="/login" element={<LoginPage />} />
        <Route path="/family/login" element={<FamilyLoginPage />} />
        <Route path="/family/invite/:token" element={<FamilyInviteAcceptPage />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/nutzungsbedingungen" element={<AGB />} />
        <Route path="/datenschutzhinweis" element={<Datenschutzerklaerung />} />
        <Route path="/" element={<SteffiHome />} />
        <Route path="/auswahl" element={<AudienceSelectionPage />} />
        <Route path="/steffi" element={<Navigate to="/angehoerige" replace />} />
        <Route path="/pflegeeinrichtungen" element={<SteffiLanding2 />} />
        <Route path="/angehoerige" element={<SteffiHome />} />
        <Route path="/newsletter" element={<SteffiHome />} />
        <Route path="/waitlist" element={<SteffiHome />} />
        <Route path="/angehoerige-alt" element={<FamilyLandingPlaceholder />} />
        <Route path="/blog" element={<PublicBlogIndex />} />
        <Route path="/blog/:slug" element={<PublicBlogPost />} />
        <Route path="/termin" element={<CalendlyWidget />} />
        <Route path="/ueber-uns" element={<AboutPage />} />
        <Route path="/reservierung" element={<ReservationPage />} />
        <Route path="/reservierung/danke" element={<ReservationPage thanks />} />

        {/* Unlisted: wake-word voice donation. Shared by link only — do not add
            this to any navigation. The page itself sets noindex. */}
        <Route path="/stimmspende" element={<Stimmspende />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
